import {Component, OnInit} from '@angular/core';
import {EnvService} from "../services/env.service";
import {HttpClient} from "@angular/common/http";
import {ActivatedRoute, Params, Router} from '@angular/router';
import {AlertService} from "../services/alert.service";
import {AuthService} from "../services/auth.service";

@Component({
  selector: 'micro-apikey-detail',
  templateUrl: './apikey-detail.component.html'
})
export class ApikeyDetailComponent implements OnInit {
  apiKey: any;

  constructor(private env: EnvService,
              private http: HttpClient,
              private route: ActivatedRoute,
              private router: Router,
              public authService: AuthService,
              private alertService: AlertService) {
  }

  ngOnInit(): void {
    this.route.params
      .subscribe((params: Params) => {
        const id: any = params['id'];
        if (id === '0') {
          this.createNew();
        } else {
          this.http.get(`${this.env.e.url}/auth/apikeys/${id}`).subscribe(
            data => this.setApiKey(data)
          );
        }
      });
  }

  setApiKey(apiKey: any): void {
    this.apiKey = apiKey;
  }

  createNew(): void {
    this.apiKey = {
      name: undefined,
      apiKey: this.newApiKey(),
      companyId: undefined,
      proxyUserId: undefined,
      services: [],
      disabled: false
    };
  }

  generateApiKey(): void {
    this.apiKey.apiKey = this.newApiKey();
  }

  copyApiKey(): void {
    const value = this.apiKey?.apiKey;
    if (typeof value !== 'string' || value.length !== 32) {
      return;
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value)
        .then(() => this.alertService.info('API key copied to clipboard'))
        .catch(() => this.copyApiKeyFallback(value));
    } else {
      this.copyApiKeyFallback(value);
    }
  }

  private copyApiKeyFallback(value: string): void {
    const input = document.createElement('textarea');
    input.value = value;
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();

    try {
      if (document.execCommand('copy')) {
        this.alertService.info('API key copied to clipboard');
      } else {
        this.alertService.warn('Could not copy API key to clipboard');
      }
    } finally {
      document.body.removeChild(input);
    }
  }

  private newApiKey(): string {
    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }

    // Set the UUID v4 version and variant bits before removing the dashes.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  save(): void {
    if (!this.apiKey.services || this.apiKey.services.length < 1) {
      this.alertService.warn('Select at least one service');
      return;
    }

    if (!this.apiKey.id) {
      this.http.post(`${this.env.e.url}/auth/apikeys`, this.apiKey)
        .subscribe(
          data => {
            this.setApiKey(data);
            this.alertService.info(`Created ${this.apiKey.name} API key`);
          }
        );
    } else {
      this.http.put(`${this.env.e.url}/auth/apikeys/${this.apiKey.id}`, this.apiKey)
        .subscribe(
          data => {
            this.setApiKey(data);
            this.alertService.info(`Updated ${this.apiKey.name} API key`);
          }
        );
    }
  }

  delete(): void {
    this.http.delete(`${this.env.e.url}/auth/apikeys/${this.apiKey.id}`)
      .subscribe(
        complete => {
          this.alertService.info(`Deleted ${this.apiKey.name} API key`);
          this.router.navigate(['/apikeys']);
        }
      );
  }
}
